import React from 'react';

/* GuideControlsPanel — central on/off switches for every "guide" layer.
   Reads/writes scenario.guides with fallback to all-on by default.
   Consumer components read scenario.guides?.<key> ?? true, so absence =
   on — nothing breaks if the key is missing. */

export const DEFAULT_GUIDES = {
  tooltips:        true,   // rich hover tooltips on tables + metrics
  eduTips:         true,   // inline glossary popovers ("?")
  drillDowns:      true,   // MoneyCell → breakdown + registry popover
  subtitles:       true,   // section descriptive lines
  modifiedBadges:  true,   // yellow override dots
  sparklineTrends: true,   // row sparkline columns on the 3-statement tables
};

export const GUIDE_DESCRIPTIONS = {
  tooltips:        { label: 'Rich tooltips',         hint: 'Hover any line or metric to see its formula, hint, explanation, and direction guide. Turn off for a dense, no-hover experience.' },
  eduTips:         { label: 'Educational popovers',  hint: 'The small "?" badge next to glossary terms (DSO, CCC, WACC, etc.). Opens a short definition on click.' },
  drillDowns:      { label: 'Drill-down popovers',   hint: 'Click any money cell → breakdown (which accounts made up this total) + scenario references (what overrides affect it).' },
  subtitles:       { label: 'Section subtitles',     hint: 'The small descriptive line under each section heading. Off = bare headings.' },
  modifiedBadges:  { label: 'Modified badges',       hint: 'Yellow dot that appears on values overridden from their default (piecewise pins, account visibility, scenario tweaks).' },
  sparklineTrends: { label: 'Trend sparklines',      hint: 'Row-level trend column with mini area charts on the 3-statement filing-style tables. Off = pure numeric table.' },
};

export default function GuideControlsPanel({ scenario, setScenario }) {
  const guides = { ...DEFAULT_GUIDES, ...(scenario?.guides ?? {}) };
  const toggle = (key) => setScenario({
    ...scenario,
    guides: { ...guides, [key]: !guides[key] },
  });
  const allOn = Object.values(guides).every(Boolean);
  const allOff = Object.values(guides).every(v => !v);

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="flex items-center gap-[8px]">
        <button
          type="button"
          className="bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={allOn}
          onClick={() => setScenario({ ...scenario, guides: { ...DEFAULT_GUIDES } })}
        >All on</button>
        <button
          type="button"
          className="bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={allOff}
          onClick={() => setScenario({
            ...scenario,
            guides: Object.fromEntries(Object.keys(DEFAULT_GUIDES).map(k => [k, false])),
          })}
        >All off</button>
        <span className="text-[0.72rem] text-text-dim ml-1">defaults: every guide on</span>
      </div>

      <ul className="list-none p-0 m-0 flex flex-col gap-[8px]">
        {Object.keys(DEFAULT_GUIDES).map((key) => {
          const on = !!guides[key];
          const { label, hint } = GUIDE_DESCRIPTIONS[key];
          return (
            <li key={key} className="grid grid-cols-[auto_1fr] gap-[12px] items-start px-[10px] py-[8px] bg-surface-el border border-border rounded-sm">
              <button
                type="button"
                className={`relative w-[38px] h-[20px] rounded-full border border-border-strong cursor-pointer transition-colors ${on ? 'bg-accent-green/60 border-accent-green' : 'bg-surface'}`}
                onClick={() => toggle(key)}
                aria-pressed={on}
                aria-label={`${label}: ${on ? 'on' : 'off'}`}
              >
                <span
                  className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all ${on ? 'left-[20px]' : 'left-[2px]'}`}
                />
              </button>
              <div className="flex flex-col">
                <strong className={`text-[0.86rem] ${on ? 'text-text' : 'text-text-dim'}`}>{label}</strong>
                <span className="text-[0.74rem] text-text-muted leading-[1.45]">{hint}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
